import * as React from 'react';

import { faEllipsis } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth/types';
import { useListValidatorAccounts } from 'src/features/validatorAccounts/api/useListValidatorAccounts';
import CreateValidatorAccountFormModal from 'src/features/validatorAccounts/components/forms/CreateValidatorAccountFormModal';
import CellValidatorAccountsActions from 'src/features/validatorAccounts/components/tables/CellValidatorAccountActions';
import { validatorAccountsMessages } from 'src/features/validatorAccounts/utils/messages';
import { ValidatorAccount } from 'src/features/validatorAccounts/utils/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { DataTable, DataTableControls, DataTablePagination } from 'src/registry/components/table';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import { Button } from 'src/registry/ui/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'src/registry/ui/DropdownMenu';
import { generalMessages } from 'src/registry/utils/messages';
import { useTitleStore } from 'src/stores/useTitleStore';
import { useParking } from 'src/providers/ParkingProvider';

function ValidatorAccountsTable() {
    const { formatMessage } = useIntl();

    const manageValidations = useHasPermissions([PERMISSION.MANAGE_VALIDATIONS]);

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'name' });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { parkingId } = useParking();

    const payload = React.useMemo(
        () => ({
            parkingId,
            ...querySearchOptions,
            ...queryPaginationOptions,
        }),
        [querySearchOptions, queryPaginationOptions, parkingId],
    );

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(validatorAccountsMessages.validationAccounts));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data: validatorAccounts, isLoading } = useListValidatorAccounts(payload);

    const columnHelper = createColumnHelper<ValidatorAccount>();

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                return (
                    <div>
                        <div className="font-medium">{row.original.name}</div>
                        <div>{row.original.description}</div>
                    </div>
                );
            },
            meta: {
                title: formatMessage(generalMessages.name),
            },
            id: 'name',
        }),
        columnHelper.accessor('users.name', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                const usersCount = ctx.row.original.users.length;

                const firstTwoUsers = ctx.row.original.users
                    ?.slice(0, 2)
                    ?.map((u) => u.name)
                    .join(', ');

                const remainingUsers = ctx.row.original.users.slice(2);

                return (
                    <div>
                        {usersCount <= 3 ? (
                            <div className="font-medium whitespace-break-spaces">
                                {ctx.row.original.users.map((u) => u.name).join(', ')}
                            </div>
                        ) : (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        className="cursor-pointer p-0 hover:bg-transparent hover:text-inherit hover:shadow-none"
                                    >
                                        <div>{firstTwoUsers}</div>
                                        <FontAwesomeIcon icon={faEllipsis} />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-full min-w-[200px]">
                                    <DropdownMenuLabel className="text-xs font-medium text-gray-600">
                                        {formatMessage(validatorAccountsMessages.users)}
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuGroup>
                                        {remainingUsers?.map((user, index) => {
                                            return (
                                                <span
                                                    key={index}
                                                    className="relative flex items-center rounded-xs px-2 py-1.5 text-sm outline-hidden transition-colors"
                                                >
                                                    {user.name}
                                                </span>
                                            );
                                        })}
                                    </DropdownMenuGroup>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                        <div>{`${usersCount} ${formatMessage(validatorAccountsMessages.total)}`}</div>
                    </div>
                );
            },
            meta: {
                title: formatMessage(validatorAccountsMessages.users),
            },
        }),
        columnHelper.accessor('validation_types_count', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                const typesCount = row.original.validation_types_count ?? 0;

                return formatMessage(validatorAccountsMessages.validationTypesCount, { count: typesCount });
            },
            meta: {
                title: formatMessage(validatorAccountsMessages.validationTypes),
            },
            maxSize: 140,
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellValidatorAccountsActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable<ValidatorAccount, any>
            isLoading={isLoading}
            columns={columns}
            data={validatorAccounts}
            search={search}
            pagination={pagination}
            semantics={{
                title: formatMessage(validatorAccountsMessages.validationAccounts),
                entryName: formatMessage(validatorAccountsMessages.account).toLowerCase(),
                entryNames: formatMessage(validatorAccountsMessages.accounts).toLowerCase(),
                placeholder: formatMessage(generalMessages.name),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(manageValidations && { newEntry: CreateValidatorAccountFormModal }),
            }}
        />
    );
}

export default ValidatorAccountsTable;
